/*=========================================================================================
  File Name: store.js
  Description: Vuex store
  ----------------------------------------------------------------------------------------
  Project: MISA MTAX WebApp
  Author: MISA JSC
  Author URL: http://www.misa.com.vn
==========================================================================================*/
import { createStore } from 'vuex';
// Global
import state from './state';
import getters from './getters';
import mutations from './mutations';
import actions from './actions';

// Module
import context from './context/moduleContext';
import employee from './moduleEmployee';
import employeeType from './dictionary/moduleEmployeeType';
import fixedAsset from './dictionary/moduleFixedAsset';
import payrollItem from './dictionary/modulePayrollItem';
import inventoryItem from './moduleInventoryItem';
import inventoryItemQuickSearch from './moduleInventoryItemQuickSearch';
import toolList from './dictionary/moduleToolList';
import accountingObject from './dictionary/moduleAccountingObject';
import accountingObjectCache from './dictionary/moduleAccountingObjectCache';
import layout from './moduleLayout';
import organizationUnit from './moduleOrganizationUnit';
import account from './dictionary/moduleAccount';
import accountCategory from './dictionary/moduleAccountCategory';
import buPlanWithDraw from './treasury/moduleBuPlanWithDraw';
import bUTransfer from './treasury/moduleBUTransfer';
import department from './dictionary/moduleDepartment';
import createPersistedState from 'vuex-persistedstate';
import currency from './dictionary/moduleCurrency';
import baWithdraw from './ba/moduleBaWithdraw';
import inventoryCategory from './dictionary/moduleInventoryCategory';
import toolType from './dictionary/moduleToolType';
import stock from './dictionary/moduleStock';
import buPlanReceipt from './treasury/moduleBuPlanReceipt';
import budgetChapter from './dictionary/moduleBudgetChapter';
import typeOfFund from './moduleTypeOfFundObject';
import bankInfoCategory from './dictionary/moduleBankInfoCategory';
import budgetGroupItem from './dictionary/moduleBudgetGroupItem';
import budgetSourceProperty from './dictionary/moduleBudgetSourceProperty';
import pAGLPayrollExpense from './pa/modulePAGLPayrollExpense';
import cAReceipt from './treasury/moduleCAReceipt';
import cAReceiptSale from './treasury/moduleCAReceiptSale';
import cAReceiptFixedAsset from './treasury/moduleCAReceiptFixedAsset';
import budgetSource from './dictionary/moduleBudgetSource';
import bankInfo from './dictionary/moduleBankInfo';
import bUVoucherList from './treasury/moduleBUVoucherList';
import budgetItem from './dictionary/moduleBudgetItem';
import budgetItemCustom from './dictionary/moduleBudgetItemCustom';
import bUPlanAdjustment from './treasury/moduleBUPlanAdjustment';
import budgetKindItem from './dictionary/moduleBudgetKindItem';
import sUTransfer from './su/moduleSUTransfer';
import approveSUTransfer from './su/moduleApproveSUTransfer';
import inInwardOutward from './inventory/moduleInInwardOutward';
import inOutward from './inventory/moduleInOutward';
import reArrangeInventory from './inventory/moduleReArrangeInventory';
import sUIncrementDecrement from './su/moduleSUIncrementDecrement';
import caReceiptDetailTax from './cash/moduleCaReceiptDetailTax';
import bUPlanWDAdjustment from './treasury/moduleBUPlanWDAdjustment';
import pUInvoice from './pu/modulePUInvoice';
import pUPayment from './pu/modulePUPayment';
import pUVendorPayment from './pu/modulePUVendorPayment';
import iNTransfer from './inventory/moduleINTransfer';
import iNAdjustment from './inventory/moduleINAdjustment';
import statistic from './dictionary/moduleStatistic';
import purchasePurpose from './dictionary/modulePurchasePurpose';
import cAPayment from './cash/moduleCaPayment';
import cAPaymentDetail from './cash/moduleCaPaymentDetail';
import cAPaymentDetailTax from './cash/moduleCaPaymentDetailTax';
import cAFundInventory from './cash/moduleCaFundInventory';
import accountTransfer from './dictionary/moduleAccountTransfer';
import bADeposit from './ba/moduleBADeposit';
import budgetFees from './dictionary/moduleBudgetFees';
import task from './dictionary/moduleTask';
import autoBusiness from './dictionary/moduleAutoBusiness';
import sAInvoice from './sa/moduleSAInvoice';
import bABankTransfer from './ba/moduleBABankTransfer';
import bUBudgetReserve from './treasury/moduleBUBudgetReserve';
import approveVoucherBUBudgetReserveTab from './treasury/moduleApproveVoucherBUBudgetReserveTab';
import gLVoucher from './gl/moduleGLVoucher';
import gLVoucherList from './gl/moduleGLVoucherList';
import approveVoucherGLBeginningYearTransferTab from './gl/moduleApproveVoucherGLBeginningYearTransferTab';
import activity from './dictionary/moduleActivity';
import accountingObjectCategory from './dictionary/moduleAccountingObjectCategory';
import customerVendor from './dictionary/moduleCustomerVendor';
import voucherType from './dictionary/moduleVoucherType';
import sAReturn from './sa/moduleSAReturn';
import invoiceFormNumber from './dictionary/moduleInvoiceFormNumber';
import bUCommitmentRequest from './treasury/moduleBUCommitmentRequest';
import bUCommitmentAdjustment from './treasury/moduleBUCommitmentAdjustment';
import bUOpeningCommitment from './treasury/moduleBUOpeningCommitment';
import project from './dictionary/moduleProject';
import targetProgram from './dictionary/moduleTargetProgram';
import projectBudgetDetail from './dictionary/moduleProjectBudgetDetail';
import contract from './dictionary/moduleContract';
import topic from './dictionary/moduleTopic';
import topicCustom from './dictionary/moduleTopicCustom';
import topicEstimate from './dictionary/moduleTopicEstimate';
import workItem from './dictionary/moduleWorkItem';
import bATransfer from './ba/moduleBATransfer';
import bUPlanImport from './treasury/moduleBUPlanImport';
import cRERepList from './cre/moduleCRERepList';
import openingSupplyEntry from './opening/moduleOpeningSupplyEntry';
import fundStructure from './dictionary/moduleFundStructure';
import qltsVoucher from './fa/moduleQltsVoucher';
import accountParallelRule from './dictionary/moduleAccountParallelRule';
import cRERepExportList from './cre/moduleCRERepExportList';
import cRERepExportTaxList from './cre/moduleCRERepExportTaxList';
import cRERepExportXML from './cre/moduleCRERepExportXML';
import cRERepSettlement from './cre/moduleCRERepSettlement';
import openingAccountEntry from './opening/moduleOpeningAccountEntry';
import openingBankAccountEntry from './opening/moduleOpeningBankAccountEntry';
import openingCustomerAccountEntry from './opening/moduleOpeningCustomerAccountEntry';
import openingSupplierAccountEntry from './opening/moduleOpeningSupplierAccountEntry';
import openingEmployeeAccountEntry from './opening/moduleOpeningEmployeeAccountEntry';
import openingInventoryEntry from './opening/moduleOpeningInventoryEntry';
import cRERepReportSubmit from './cre/moduleCRERepReportSubmitList';

import defaultAccount from './dictionary/moduleDefaultAccount';
import dbOption from './moduleDbOption';
import salaryScale from './dictionary/moduleSalaryScale';
import openingAccountEntryStatistic from './opening/moduleOpeningAccountEntryStatistic';

import payrollItemColumn from './dictionary/modulePayrollItemColumn';
import pAOption from './dictionary/modulePAOptionsss';

import budgetProvide from './dictionary/moduleBudgetProvide';

import accountParallelRuleDetail from './dictionary/moduleAccountParallelRuleDetail';
import budgetSourceTranfer from './dictionary/moduleBudgetSourceTranfer';
import accountingObjectChangeRate from './dictionary/moduleAccountingObjectChangeRate';
import stateBank from './dictionary/moduleStateBank';
import commercialBank from './dictionary/moduleCommercialBank';
import accountChangeTranfer from './dictionary/moduleAccountChangeTranfer';
import budgetKindItemTranfer from './dictionary/moduleBudgetKindItemTranfer';
import projectTranfer from './dictionary/moduleProjectTranfer';

import sysAccountParallelVoucherListRule from './dictionary/moduleSysAccountParallelVoucherListRule';
import sysAccountParallelVoucherListRuleDetail from './dictionary/moduleSysAccountParallelVoucherListRuleDetail';

//Màn hình chọn chứng từ mua, nhập VTHH
import cAReceiptSaleUnitCapitalList from './sa/moduleCAReceiptSaleUnitCapitalList';

//Voucher
import voucher05TT from './voucher/moduleVoucher05TT';

import sASaleInvoice from './sa/moduleSASaleInvoice';
import pASalary from './pa/modulePASalary';
import pALayoutTemplate from './pa/modulePALayoutTemplate';
import pAPersonalIncomeTax from './pa/modulePAPersonalIncomeTax';

import pAOtherInputSalary from './pa/modulePAOtherInputSalary'; // vqphong - 17.10.2022

/**begin Store cho cho chức năng Duyệt chứng từ*/
import approveVoucherCAReceiptPlanWithdrawList from './treasury/moduleApproveVoucherCAReceiptPlanWithdrawList';
import approveVoucherBUCommitmentAdjustmentTab from './treasury/moduleApproveVoucherBUCommitmentAdjustmentTab';
import approveVoucherBUPlanReceipt from './treasury/moduleApproveVoucherBUPlanReceipt';
import approveVoucherBAWithDrawTab from './ba/moduleApproveVoucherBAWithDrawTab';
import approveVoucherBUOpeningCommitmentTab from './treasury/moduleApproveVoucherBUOpeningCommitmentTab';
import approveVoucherBUCommitmentRequestTab from './treasury/moduleApproveVoucherBUCommitmentRequestTab';

import approveVoucherBADepositTab from './ba/moduleApproveVoucherBADepositTab';

import approveVoucherBUPlanCancelTab from './treasury/moduleApproveVoucherBUPlanCancelTab';
import approveVoucherBABankTransferTab from './ba/moduleApproveVoucherBABankTransferTab';
import approveVoucherSAReturnList from './sa/moduleApproveVoucherSAReturnList';
import approveVoucherCAReceiptList from './treasury/moduleApproveVoucherCAReceiptList';
import approveVoucherCAReceiptCustomer from './cash/moduleApproveVoucherCAReceiptCustomer.js';
import approveVoucherPUInvoiceList from './inventory/moduleApproveVoucherPUInvoiceList';

import approveVoucherBUTransferTab from './treasury/moduleApproveVoucherBUTransferTab';
import approveVoucherPUPaymentTab from './pu/moduleApproveVoucherPUPaymentTab';
import approveVoucherFAPurchaseIncrementList from './fixedAsset/moduleApproveVoucherFAPurchaseIncrementList';
import approveVoucherBUPlanWithdrawTab from './treasury/moduleApproveVoucherBUPlanWithdrawTab';
import approveVoucherCAPayment from './cash/moduleApproveVoucherCAPayment';
import approveVoucherSAInvoiceTab from './sa/moduleApproveVoucherSAInvoiceTab';
import approveVoucherINInwardTab from './treasury/moduleApproveVoucherINInwardTab';
import approveVoucherINTransferList from './inventory/moduleApproveVoucherINTransferList';
import approveVoucherInInwardOutward from './inventory/moduleApproveVoucherInInwardOutward';
import approveVoucherPUVendorPaymentTab from './pu/moduleApproveVoucherPUVendorPaymentTab';
import approveVoucherINAdjustmentList from './inventory/moduleApproveVoucherINAdjustmentList';
import approveVoucherBUPlanAdjustmentTab from './treasury/moduleApproveVoucherBUPlanAdjustmentTab';
import approveVoucherBUPlanWDAdjustmentTab from './treasury/moduleApproveVoucherBUPlanWDAdjustmentTab';
import approveVoucherSASaleInvoiceTab from './sa/moduleApproveVoucherSASaleInvoiceTab';
import approveVoucherReceiptList from './sa/moduleApproveVoucherReceiptList';
import approveElectronicTicketList from './sa/moduleApproveElectronicTicketList';
import approveVoucherQltsVoucher from './fa/moduleApproveVoucherQltsVoucher';
import approveVoucherGLEndYearTransferTab from './gl/moduleApproveVoucherGLEndYearTransferTab';
import approveVoucherFAAdjustmentList from './fa/moduleApproveVoucherFAAdjustmentList';
import approveVoucherFATransferList from './fixedAsset/moduleApproveVoucherFATransferList.js';
import approveVoucherCAReceiptFixedAsset from './fixedAsset/moduleApproveVoucherCAReceiptFixedAsset';

/**end Store cho cho chức năng Duyệt chứng từ*/
import approveVoucherGLVoucherTab from './gl/moduleApproveVoucherGLVoucherTab';
import approveVoucherBATransferTab from './gl/moduleAproveBATransferTab';

import approveVoucherGLProfitLossTransferVoucher from './gl/moduleApproveVoucherGLProfitLossTransferVoucher.js';

import gLProfitLossTransferVoucher from './gl/moduleGLProfitLossTransferVoucher.js';

import webSocketGlobal from './global/moduleWebSocketGlobal.js';

import choseFinanceReportSetting from './fr/moduleChoseFinanceReportSetting';
import frTemplateCustom from './fr/moduleFrTemplateCustom';
import formulaCustom from './fr/moduleFRFormulaCustom';
import columnList from './fr/moduleColumnList';

import sASaleInvoiceReference from './sa/moduleSASaleInvoiceReference';
import sASaleInvoiceStatus from './sa/moduleSASaleInvoiceStatus';
import sASaleInvoiceCancel from './sa/moduleSASaleInvoiceCancel';
import dVCFile from './dvc/moduleDVCFile';
import dVCFileDetail from './dvc/moduleDVCFileDetail';
import stateTreasury from './dictionary/moduleStateTreasury';
import notification from './global/moduleNotification';
import dVCFileC202 from './dvc/moduleDVCFileC202';
import dVCFileC203 from './dvc/moduleDVCFileC203';
import dVCFileC205 from './dvc/moduleDVCFileC205';
import dVCFileC402 from './dvc/moduleDVCFileC402';
import dVCFileM01 from './dvc/moduleDVCFileM01';

import pAStatisticalDayOff from './pa/modulePAStatisticalDayOff';
import voucherSearch from './voucher/moduleVoucherSearch.js';
import pASortEmployee from './pa/modulePASortEmployee';
import pAEmployeeHistory from './pa/modulePAEmployeeHistory';

import diCacheConfig from './dictionary/moduleCacheConfig.js';
import accountCombo from './dictionary/moduleAccountCombo.js';
import autoBusinessCache from './dictionary/moduleAutoBusinessCache.js';

import staticReportPrint from './dictionary/moduleStaticReport.js';
import sYSAuditingLog from './sys/moduleSYSAuditingLog.js';

import convertDataCompare from './convert/moduleConvertDataCompare.js';
import approveGLPayrollExpenseTab from './gl/moduleApproveGLPayrollExpenseTab.js';

import sysUser from './dictionary/moduleSysUser.js';
import confirmSysUser from './dictionary/moduleConfirmSysUser';
import sysRole from './dictionary/moduleSysRole.js';
import fAAdjustment from './fixedAsset/moduleFAAdjustment.js';
import fAIncrementDecrement from './fixedAsset/moduleFAIncrementDecrement.js';
import fADecrement from './fixedAsset/moduleFADecrement.js';
import fAPurchaseIncrement from './fixedAsset/moduleFAPurchaseIncrement.js';
import fixedAssetCategory from './dictionary/moduleFixedAssetCategory.js';
import fixedAssetCategoryCustom from './dictionary/moduleFixedAssetCategoryCustom.js';
import fADevaluation from './fixedAsset/moduleFADevaluationList.js';
import approveVoucherFADevaluationList from './fixedAsset/moduleApproveVoucherFADevaluationList.js';

import approveVoucherSUIncrement from './su/moduleApproveSUIncrement';
import fixedAssetCache from './dictionary/moduleFixedAssetCache';

import fAIncrement from './fixedAsset/moduleFAIncrement.js';
import fATransfer from './fixedAsset/moduleFATransfer.js';

// Đối chiếu số dư KBDT
import dCSDFile from './dvc/moduleDCSDFile';
// Đối chiếu số dư KBDT chi tiết
import dCSDFileDetail from './dvc/moduleDCSDFileDetail';

import approveVoucherFAIncrementList from './fixedAsset/moduleApproveVoucherFAIncrementList.js';
import approveVoucherFADecrementList from './fixedAsset/moduleApproveVoucherFADecrementList.js';

import fADepreciation from './fixedAsset/moduleFADepreciation.js';
// thuế
import tACircularVATDeclaration from './ta/moduleTACircularVATDeclaration.js';
import tACircularVATDeclarationDirect from './ta/moduleTACircularVATDeclarationDirect.js';
import tAVATDeclarationAppendix from './ta/moduleTAVATDeclarationAppendix.js';
import taRiskCustomerVendor from './ta/moduleTARiskCustomerVendor.js';
import organizationHaveRisk from './ta/moduleOrganizationHaveRisk.js';
import tAEnterpriseIncomeTax from './ta/moduleTAEnterpriseIncomeTax.js';
import backupDatabase from './master/moduleBackupDatabase';
import rpMergeReport from './rp/moduleMergeReport.js';

import bUB58KTNN from './treasury/moduleB58KTNN.js';
/**
 * Module cho phần Chọn chứng từ
 */
import chooseVoucherReportFADepreciation from './fixedAsset/moduleChooseVoucherReportFADepreciation';
import chooseVoucherReportGLVoucherSummaryTable from './gl/moduleChooseVoucherReportGLVoucherSummaryTable';

import cREREPForSuperiorPopup from './cre/moduleCREREPForSuperiorPopup';

import sAReceipt from './sa/moduleSAReceipt';
import sAElectronicTicket from './sa/moduleSAElectronicTicket';

import restoreVoucher from './sys/moduleRestoreVoucher';

import collectionAgency from './moduleCollectionAgency';

import tempFixedAssetCategory from './dictionary/moduleTempFixedAssetCategory';

import moduleSurplusDeficit from './dashboard/moduleSurplusDeficit';
import moduleBudgetSituation from './dashboard/moduleBudgetSituation';
import moduleResultActivity from './dashboard/moduleResultActivity';

import modulePayableDebt from './dashboard/modulePayableDebt';

import moduleUsingSaving from './dashboard/moduleUsingSaving';

import moduleUsingIncomeAndExpense from './dashboard/moduleUsingIncomeAndExpense';

import moduleReceivableDebt from './dashboard/moduleReceivableDebt';
import moduleCashBalanceDeposit from './dashboard/moduleCashBalanceDeposit';

import moduleBudgetPlanImplementation from './dashboard/moduleBudgetPlanImplementation';

import moduleOrganizationShareFormula from './shareReportConfig/moduleOrganizationShareFormula';

import moduleChoseLayoutReportSetting from './fr/moduleChoseLayoutReportSetting';

// Phục vụ làm việc ngoại tuyến
import moduleOfflineWork from './pwa/moduleOfflineWork';

import moduleOfflineVoucher from './pwa/moduleOfflineVoucher';

// invoiceBot
import moduleInvoiceBot from './pu/moduleInvoiceBot';

import defaultAccountCache from './dictionary/moduleDefaultAccountCache';
import lgLedgerOriginal from './moduleOriginalLedger';
import dictionaryOption from './dictionary/moduleDictionaryOption';
// Init instance of store
export default createStore({
  getters,
  mutations,
  state,
  actions,
  modules: {
    accountCombo,
    autoBusinessCache,
    context,
    employee,
    employeeType,
    fixedAsset,
    payrollItem,
    layout,
    organizationUnit,
    inventoryItem,
    inventoryItemQuickSearch,
    toolList,
    accountingObject,
    accountingObjectCache,
    account,
    buPlanWithDraw,
    bUTransfer,
    department,
    currency,
    baWithdraw,
    inventoryCategory,
    stock,
    buPlanReceipt,
    budgetChapter,
    typeOfFund,
    bankInfoCategory,
    budgetGroupItem,
    budgetSourceProperty,
    cAReceipt,
    budgetSource,
    bankInfo,
    bUVoucherList,
    budgetItem,
    budgetItemCustom,
    bUPlanAdjustment,
    budgetKindItem,
    sUTransfer,
    approveSUTransfer,
    inInwardOutward,
    inOutward,
    reArrangeInventory,
    sUIncrementDecrement,
    caReceiptDetailTax,
    bUPlanWDAdjustment,
    pUInvoice,
    pUPayment,
    pUVendorPayment,
    iNTransfer,
    iNAdjustment,
    statistic,
    purchasePurpose,
    cAPayment,
    cAPaymentDetail,
    cAPaymentDetailTax,
    cAFundInventory,
    budgetFees,
    accountTransfer,
    bADeposit,
    task,
    autoBusiness,
    sAInvoice,
    bABankTransfer,
    bUBudgetReserve,
    gLVoucher,
    gLVoucherList,
    approveVoucherGLBeginningYearTransferTab,
    activity,
    accountingObjectCategory,
    customerVendor,
    invoiceFormNumber,
    voucherType,
    sAReturn,
    invoiceFormNumber,
    bUCommitmentRequest,
    bUCommitmentAdjustment,
    bUOpeningCommitment,
    targetProgram,
    project,
    projectBudgetDetail,
    contract,
    topic,
    topicCustom,
    topicEstimate,
    workItem,
    bATransfer,
    bUPlanImport,
    cRERepList,
    openingSupplyEntry,
    fundStructure,
    qltsVoucher,
    accountParallelRule,
    cRERepExportList,
    defaultAccount,
    cRERepSettlement,
    cAReceiptSale,
    cAReceiptFixedAsset,
    dbOption,
    cRERepExportTaxList,
    cRERepExportXML,
    openingAccountEntry,
    openingBankAccountEntry,
    openingCustomerAccountEntry,
    openingSupplierAccountEntry,
    openingEmployeeAccountEntry,
    openingInventoryEntry,
    salaryScale,
    payrollItemColumn,
    openingAccountEntryStatistic,
    pAOption,
    budgetProvide,
    accountParallelRuleDetail,
    budgetSourceTranfer,
    toolType,
    accountingObjectChangeRate,
    accountChangeTranfer,
    budgetKindItemTranfer,
    stateBank,
    commercialBank,
    projectTranfer,
    sysAccountParallelVoucherListRule,
    sysAccountParallelVoucherListRuleDetail,
    cAReceiptSaleUnitCapitalList,
    sysUser,
    confirmSysUser,
    fixedAssetCache,
    //Voucher
    voucher05TT,
    pAStatisticalDayOff,
    voucherSearch,
    pASortEmployee,
    pAEmployeeHistory,
    pAOtherInputSalary,
    sASaleInvoice,

    pASalary,
    pALayoutTemplate,

    pAPersonalIncomeTax,
    pAOtherInputSalary,
    pAGLPayrollExpense,

    /**begin Store cho chức năng Duyệt chứng từ*/
    approveVoucherCAReceiptPlanWithdrawList,
    approveVoucherBAWithDrawTab,
    approveVoucherBUCommitmentAdjustmentTab,
    approveVoucherBADepositTab,
    approveVoucherBUOpeningCommitmentTab,
    approveVoucherBUCommitmentRequestTab,
    approveVoucherBUPlanReceipt,
    approveVoucherBUPlanCancelTab,
    approveVoucherBUBudgetReserveTab,
    approveVoucherBABankTransferTab,
    approveVoucherSAReturnList,
    approveVoucherCAReceiptList,
    approveVoucherBUTransferTab,
    approveVoucherPUPaymentTab,
    approveVoucherFAPurchaseIncrementList,
    approveVoucherBUPlanWithdrawTab,
    approveVoucherCAPayment,
    approveVoucherSAInvoiceTab,
    approveVoucherCAReceiptCustomer,
    approveVoucherINInwardTab,
    approveVoucherPUInvoiceList,
    approveVoucherINTransferList,
    approveVoucherInInwardOutward,
    approveVoucherINAdjustmentList,
    approveVoucherBUPlanAdjustmentTab,
    approveVoucherBUPlanWDAdjustmentTab,
    approveVoucherPUVendorPaymentTab,
    approveVoucherQltsVoucher,
    approveVoucherGLEndYearTransferTab,
    approveVoucherSASaleInvoiceTab,
    approveVoucherReceiptList,
    approveElectronicTicketList,
    approveVoucherFAAdjustmentList,
    approveVoucherFAIncrementList,
    approveVoucherFADecrementList,
    approveVoucherFATransferList,
    approveVoucherCAReceiptFixedAsset,
    /**end Store cho chức năng Duyệt chứng từ*/

    approveVoucherGLVoucherTab,
    approveVoucherBATransferTab,
    sASaleInvoiceReference,
    sASaleInvoiceStatus,
    sASaleInvoiceCancel,
    dVCFile,
    dVCFileDetail,
    dVCFileC202,
    dVCFileC203,
    dVCFileC205,
    dVCFileC402,
    dVCFileM01,

    approveVoucherGLProfitLossTransferVoucher,
    gLProfitLossTransferVoucher,
    choseFinanceReportSetting,
    frTemplateCustom,
    formulaCustom,
    columnList,

    webSocketGlobal,
    stateTreasury,

    notification,
    diCacheConfig,
    staticReportPrint,
    sYSAuditingLog,

    convertDataCompare,
    approveGLPayrollExpenseTab,
    sysRole,
    fAAdjustment,
    fAIncrementDecrement,
    fADecrement,
    fAPurchaseIncrement,
    fixedAssetCategory,
    fixedAssetCategoryCustom,
    approveVoucherSUIncrement,
    dCSDFileDetail,
    fAIncrement,
    dCSDFile,
    fADepreciation,
    fATransfer,
    fADevaluation,
    approveVoucherFADevaluationList,
    tACircularVATDeclaration,
    tACircularVATDeclarationDirect,
    tAEnterpriseIncomeTax,
    rpMergeReport,
    tAVATDeclarationAppendix,
    taRiskCustomerVendor,
    accountCategory,
    bUB58KTNN,
    backupDatabase,
    chooseVoucherReportFADepreciation,
    chooseVoucherReportGLVoucherSummaryTable,
    cRERepReportSubmit,
    cREREPForSuperiorPopup,
    restoreVoucher,
    sAReceipt,
    sAElectronicTicket,
    collectionAgency,
    tempFixedAssetCategory,
    moduleSurplusDeficit,
    moduleBudgetSituation,
    modulePayableDebt,
    moduleReceivableDebt,
    moduleResultActivity,
    moduleBudgetPlanImplementation,
    moduleCashBalanceDeposit,
    moduleOrganizationShareFormula,
    moduleUsingIncomeAndExpense,
    moduleUsingSaving,
    moduleChoseLayoutReportSetting,
    // Làm việc offline
    moduleOfflineWork,
    moduleOfflineVoucher,
    // invoicebot
    moduleInvoiceBot,
    defaultAccountCache,
    lgLedgerOriginal,
    organizationHaveRisk,
    dictionaryOption,
  },
  strict: false,
  plugins: [
    createPersistedState({
      key: 'mimosa_vuex',
      paths: ['context'],
      getState: key => {
        let temp = localStorage.getItem(key);
        if (temp && typeof temp === 'string') {
          let state = JSON.parse(temp);
          if (state) {
            let context = state.context;
            if (context && context.Token) {
              context.Context = context.Context || {};

              try {
                context.TokenExpired = new Date(context.TokenExpired);

                if (context.PostedDate) {
                  context.PostedDate = new Date(context.PostedDate);
                } else {
                  let now = new Date();
                  context.PostedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                }
              } catch (ex) {
                console.error(ex);
              }
            }
          }

          return state;
        }
        return null;
      },
      setState: (key, state) => {
        localStorage.setItem(key, JSON.stringify(state));
      },
    }),
  ],
});
